import { parseDate } from 'lib/date';
import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions, AIJobStatus } from 'models/AIJob';
import AIPrompt, { AIPromptOptions } from 'models/AIPrompt';
import Attachment, { AttachmentOptions } from 'models/Attachment';
import User, { UserOptions } from 'models/User';

export type SummarizationPatient = {
  name: string;
  gender: string;
  dateOfBirth: string;
};

export type SummarizationData = {
  patient: SummarizationPatient;
  [key: string]: any;
};

type LatestSummaryJob = {
  id: string;
  status: AIJobStatus | null;
};

export interface SummarizationOptions {
  id: string;
  title: string;
  data: SummarizationData;
  archived: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  hasBeenViewed: boolean;
  lastSeenAt: Date | string;
  aiPromptId: string;
  createdBy?: Partial<UserOptions> | null;
  updatedBy?: Partial<UserOptions> | null;
  attachmentCount?: number;
  attachments?: Partial<AttachmentOptions>[];
  aiPrompt?: Partial<AIPromptOptions> | null;
  patientDemographicsJob?: AIJobOptions | null;
  latestSummaryJob?: LatestSummaryJob | null;
  summaryJobForCurrentAttachments?: AIJobOptions | null;
}

function getDefaults(): SummarizationOptions {
  return {
    id: '',
    title: '',
    data: {
      patient: {
        name: '',
        gender: '',
        dateOfBirth: '',
      },
    },
    archived: false,
    createdAt: '',
    updatedAt: '',
    hasBeenViewed: false,
    lastSeenAt: '',
    aiPromptId: '',
    createdBy: undefined,
    updatedBy: undefined,
    attachmentCount: 0,
    attachments: [],
  };
}

/**
 * @class Summarization
 * @classdesc Represents a standalone summarization in the system (not tied to patient context)
 * @property {string} id - The summarization's id
 * @property {string} title - The summarization's title
 * @property {SummarizationData} data - The summarization data containing patient info and other key-value pairs
 * @property {boolean} archived - Whether the summarization is archived
 * @property {Date | null} createdAt - The summarization's creation date
 * @property {Date | null} updatedAt - The summarization's last update date
 * @property {boolean} hasBeenViewed - Whether the summarization has been viewed before
 * @property {Date | null} lastSeenAt - The summarization's mostly recently viewed timestamp
 * @property {string} aiPromptId - The ID of the AI prompt used for summarization
 * @property {UserOptions} createdBy - The user who created the summarization
 * @property {UserOptions} updatedBy - The user who last updated the summarization
 * @property {number} attachmentCount - The number of attachments associated with the summarization
 * @property {Attachment[]} attachments - Files attached to the summarization
 * @property {AIPrompt | null} aiPrompt - The AI prompt used for summarization
 * @property {AIJob | null} patientDemographicsJob - The AI job for retrieving patient demographics
 * @property {LatestSummaryJob | null} latestSummaryJob - The id and status of the AI job for generating the summary
 * @property {AIJob | null} summaryJobForCurrentAttachments - The AI job for generating the summary
 * @param {Partial<SummarizationOptions>} [options={}]
 * @example const summarization = new Summarization({ id: '123', title: 'Patient Summary' });
 */
export default class Summarization {
  id: string;
  title: string;
  data: any;
  archived: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
  hasBeenViewed: boolean;
  lastSeenAt: Date | null;
  aiPromptId: string;
  createdBy?: UserOptions;
  updatedBy?: UserOptions;
  attachmentCount?: number;
  attachments: Attachment[];
  aiPrompt?: AIPrompt | null;
  patientDemographicsJob: AIJob | null;
  latestSummaryJob?: LatestSummaryJob | null;
  summaryJobForCurrentAttachments: AIJob | null;

  constructor(options: Partial<SummarizationOptions>) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.title = opts.title;
    this.data = opts.data;
    this.archived = opts.archived;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.updatedAt = parseDate(opts.updatedAt) ?? null;
    this.hasBeenViewed = opts.hasBeenViewed;
    this.lastSeenAt = parseDate(opts.lastSeenAt) ?? null;
    this.aiPromptId = opts.aiPromptId;
    this.createdBy = opts.createdBy ? new User(opts.createdBy) : undefined;
    this.updatedBy = opts.updatedBy ? new User(opts.updatedBy) : undefined;
    this.aiPrompt = opts.aiPrompt ? new AIPrompt(opts.aiPrompt) : undefined;
    this.attachmentCount = opts.attachmentCount;
    this.attachments = opts.attachments?.map((a) => new Attachment(a)) ?? [];
    this.patientDemographicsJob = opts.patientDemographicsJob ? new AIJob(opts.patientDemographicsJob) : null;
    this.latestSummaryJob = opts.latestSummaryJob;
    this.summaryJobForCurrentAttachments = opts.summaryJobForCurrentAttachments
      ? new AIJob(opts.summaryJobForCurrentAttachments)
      : null;
  }

  get patient(): Partial<SummarizationPatient> | null {
    return this.data.patient;
  }

  get summaryJob(): AIJob {
    return this.summaryJobForCurrentAttachments as AIJob;
  }

  get summary(): Summary | undefined {
    return this.summaryJob?.aiArtifact as Summary | undefined;
  }
}
